import React from 'react';
import { Alert } from '@material-ui/lab';
import { Block, Typography, ActionButton } from '@klp/design';
import { IconName } from '@klp/design/Illustrations';
import { useTranslation } from 'react-i18next';
import type { Aktivitet } from '@klp/formgenerator/interfaces';
import type { Bruker } from '@klp/user/types';
import { useFormAuth } from '@klp/formgenerator/hooks';
import { getLocalizedRoles } from '@klp/formgenerator/utils';

type FormAlertProps = {
    activity: Aktivitet;
    currentUser: Bruker | undefined;
    handleAssignResponsibleUser: (
        userEmail: string,
        comment?: string,
        redirectTo?: string,
        endreProsessAnsvarlig?: boolean,
    ) => void;
    assigningTask: boolean;
    registreringStatus?: string;
};

export const FormAlert: React.FC<FormAlertProps> = ({
    activity,
    currentUser,
    handleAssignResponsibleUser,
    assigningTask,
    registreringStatus,
}) => {
    const { t } = useTranslation();
    const activityCompleted =
        activity.id !== null && activity.utfortDato !== null;
    const activityAssignedToOtherUser =
        activity.tildeltTil !== currentUser?.epost;
    const activityRejected =
        registreringStatus === 'Avvist' && activityCompleted;
    const { matchCurrentUserToCandidateGroup } = useFormAuth();

    const handleAssignTaskToMe = () => {
        if (!currentUser) return;

        handleAssignResponsibleUser(
            currentUser.epost,
            undefined,
            undefined,
            false,
        );
    };

    if (!currentUser) {
        return null;
    }

    if (activityRejected) {
        return (
            <Block paddingBottom="tiny">
                <Alert severity="error">
                    <Typography>{t('prosjekt.rejectedActivity')}</Typography>
                </Alert>
            </Block>
        );
    }

    if (activityCompleted) {
        return (
            <Block paddingBottom="tiny">
                <Alert severity="success">
                    <Typography>{t('prosjekt.completedActivity')}</Typography>
                </Alert>
            </Block>
        );
    }

    if (currentUser?.isEDAdmin) {
        return (
            <Block paddingBottom="tiny">
                <Alert severity="info">
                    <Typography>
                        {t('prosjekt.adminInformation', {
                            role: getLocalizedRoles(activity.kandidatGruppe),
                            assigned: activity.tildeltTil,
                        })}
                    </Typography>
                </Alert>
            </Block>
        );
    }

    if (!matchCurrentUserToCandidateGroup(activity.kandidatGruppe)) {
        return (
            <Block paddingBottom="tiny">
                <Alert severity="warning">
                    <Typography>
                        {t('prosjekt.insufficientUserRole', {
                            role: getLocalizedRoles(activity.kandidatGruppe),
                        })}
                    </Typography>
                </Alert>
            </Block>
        );
    }

    if (activityAssignedToOtherUser) {
        return (
            <Block paddingBottom="tiny">
                <Alert severity="warning">
                    <Typography>
                        {t('prosjekt.activityAssignedToOtherUser', {
                            assigned: activity.tildeltTil,
                        })}
                    </Typography>
                    <Block paddingTop="compact">
                        <ActionButton
                            isLoading={assigningTask}
                            startIcon={IconName.PROFILE}
                            actionType="button"
                            translation="prosjekt.assignActivityToMe"
                            onClick={handleAssignTaskToMe}
                        />
                    </Block>
                </Alert>
            </Block>
        );
    }

    return null;
};
